import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { APPROACH_STEPS, IMAGES, PRODUCTS, SERVICES } from "../data/content";
import { TEAM } from "../data/team";
import { TeamCard } from "./Team";

export default function About() {
  return (
    <>
      <SEO
        title="About Us | ZasyaOnline"
        description="ZasyaOnline Pvt Ltd is a product-led technology company combining proprietary software products with consulting, custom development, integration and managed technology services."
        path="/about"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="about-hero">
        <div className="container-x grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow">About ZasyaOnline</span>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
                A product company that also does the work.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
                ZasyaOnline Pvt Ltd builds and operates its own software products —
                MoveOS, myBantu, LearnSphere and CraneSoftwares — and applies the same
                engineering practice to consulting, custom development, integration
                and managed technology services for enterprises.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">
                That combination matters. Our products keep us honest about what it
                takes to ship and operate software in production; our consulting work
                keeps our products grounded in how businesses actually run.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="clip-frame overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5">
              <img
                src={IMAGES.teamWhiteboard}
                alt="ZasyaOnline team working through a solution design"
                className="h-72 w-full object-cover sm:h-96"
                loading="eager"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="about-approach">
        <div className="container-x">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Our approach</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Discovery, design, delivery, improvement.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {APPROACH_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.07} className="h-full">
                <div className="card-line relative h-full overflow-hidden p-6">
                  <span className="absolute -right-2 -top-4 font-display text-7xl font-extrabold text-slate-100" aria-hidden="true">
                    {s.step}
                  </span>
                  <h3 className="relative font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="relative mt-2 text-sm leading-relaxed text-slate-600">{s.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="about-capabilities">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow">What we bring</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Products and practice, under one roof.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Every engagement draws on both sides of the company: the products we
              operate and the services we deliver.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" className="btn-primary" data-testid="about-products-link">
                Our products <ArrowRight size={15} aria-hidden="true" />
              </Link>
              <Link to="/consulting" className="btn-secondary" data-testid="about-consulting-link">
                Our services
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2 lg:col-span-7">
            <Reveal className="h-full">
              <div className="card-line h-full p-6">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-700">Products</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
                  {PRODUCTS.map((p) => (
                    <li key={p.id}>
                      <Link to={p.path} className="font-semibold text-slate-900 hover:text-blue-700">
                        {p.name}
                      </Link>{" "}
                      — <span className="text-slate-500">{p.category}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div className="card-line h-full p-6">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal-700">Services</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
                  {SERVICES.map((s) => (
                    <li key={s.id}>{s.title}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="about-team">
        <div className="container-x">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="eyebrow">Team</span>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                The people behind the products.
              </h2>
            </div>
            <Link to="/team" className="btn-secondary" data-testid="about-team-link">
              Meet the team <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.slice(0, 6).map((m, i) => (
              <TeamCard key={m.initials} member={m} index={i} compact />
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Work with us"
        title="Meet the team behind the products."
        body="Start with a demo or a conversation — either way, you'll talk to people who build and run this software."
      />
    </>
  );
}
